import fileDownload from 'js-file-download';

const OfferManager = require('offer-manager');
const bowser = require('bowser');

export const support = {
  fetch: (...args) => window.fetch(...args),
  fileDownload,
  OfferManager,
};

export default class ExportDocument {
  static getDocumentName() {
    // Use either the original PDF name of the document if it's available.
    // Otherwise just give it a generic Name
    let documentName = 'FormSwift-Document';
    if (window.DocumentSettings.name) {
      documentName = window.DocumentSettings.name;
    }

    return documentName;
  }

  static handleExportPermissionsFailure(resp) {
    if (resp.next_action === 'show_reactivation_modal') {
      const offerManager = new OfferManager();
      offerManager.showReactivationModal();
    } else {
      window.location = self.newAccountPath + window.location.search;
    }
  }

  static isFallbackAutoPrintRequired(autoPrint) {
    return bowser.chrome && autoPrint;
  }

  static enableFallbackAutoPrint(autoPrint) {
    if (ExportDocument.isFallbackAutoPrintRequired(autoPrint)) {
      // Targeting an iframe with src about:blank to be as specific as possible here since this
      // iframe is created by jquery plugin, fileDownload, which does not give the iframe a class
      // or id to target.  In the case that we add another iframe to the fw builder, it's unlikely
      // it will also have src about:blank, so we will still select the printing iframe created by
      // 'fileDownload' and ignore the new iframe.
      const iframeTargeting = "iframe[src='about:blank']";
      const printIframes = document.querySelectorAll(iframeTargeting);
      const printableContentWindow = printIframes.length
        ? printIframes[printIframes.length - 1].contentWindow
        : null;

      if (printableContentWindow) {
        printableContentWindow.print();
      }
    }
  }

  static deductCredit() {
    // no need to deduct credits for shared documents
    if (window.DocumentSettings && window.DocumentSettings.shareKey) {
      return;
    }
    if (typeof window.FreemiumBox !== 'undefined') {
      window.FreemiumBox.deductCredit();
    }
  }

  static stopExport(cancelled, autoPrint) {
    // This works at the end as a cleanup, and also while PDFJS is rendering pages.
    // Trigger after event cleanup
    const event = document.createEvent('CustomEvent');
    event.initCustomEvent('afterprint', false, false, 'custom');
    window.dispatchEvent(event);

    // Hide the print container
    $('#printContainer').show();

    ExportDocument.exportLoading('stop');

    // don't show download overlay for cancelled or printing
    if (!(autoPrint || cancelled)) {
      window.DownloadOverlayManager.show();
    }
  }

  static async exportLoading(event, format, callback) {
    // Display/hide the modal
    if (event === 'start') {
      // Stop auto-save during export
      window.ReactCompatibility.stopFreeWriteAutoSave();

      // Save the current state of data right before we do the export
      await window.ReactCompatibility.saveFreeWriteContent();

      if (callback) {
        callback();
      }
      window.ReactCompatibility.openFreeWriteExportDocumentModal(format);
    } else if (event === 'stop') {
      setTimeout(() => {
        window.ReactCompatibility.startFreeWriteAutoSave();
      }, 5000);

      window.ReactCompatibility.closeFreeWriteExportDocumentModal();

      // For Onboarding Tour
      if (window.OnboardingTour) {
        window.COOKIE.set('documentExported', 1);
      }
    }
  }

  constructor(
    documentType,
    sessionId,
    sessionHash,
    forceDownload,
    isMembersAreaExport,
  ) {
    this.newAccountPath = '/new-account-for-doc-fw';
    this.generateDocXViaPngOverlay = true;
    this.documentType = documentType;
    this.sessionId = sessionId;
    this.sessionHash = sessionHash;
    this.forceDownload = forceDownload;
    this.isMembersAreaExport = isMembersAreaExport;
  }

  getDownloadParamString(paramStr) {
    let param = paramStr;

    const key = this.sessionId;
    const ses = this.sessionHash;
    const documentType = this.documentType;
    // This is for pages with download such as myDocuments, used to setup params correctly
    if (this.sessionId && this.sessionHash && this.documentType) {
      param = `${param}&documentType=${documentType}&ses=${ses}&key=${key}`;
    }
    if (window.DocumentSettings.shareKey) {
      param = `${param}&shareKey=${window.DocumentSettings.shareKey}`;
    }

    return param;
  }

  getParamString(format, password, autoPrint) {
    let paramStr = `${window.location.search.substr(1)}&format=${format}`;

    if (password) {
      paramStr = `${paramStr}&password=${password}`;
    }

    // Disable auto-print for Firefox. (https://bugzilla.mozilla.org/show_bug.cgi?id=911444)
    // FF does not support the PDF print function.
    // It fails silently without downloading or displaying the PDF
    const autoPrintSupported =
      navigator.userAgent.search('Firefox') === -1 &&
      navigator.userAgent.search('Edge') === -1;

    if (autoPrint && autoPrintSupported) {
      paramStr = `${paramStr}&auto_print=${autoPrint}`;
    }

    paramStr = this.getDownloadParamString(paramStr);

    return paramStr;
  }

  getSearchParams() {
    let searchParams = null;
    const forceDownloadPair = this.forceDownload ? '&force_download=1' : '';
    if (this.sessionId && this.sessionHash && this.documentType) {
      searchParams = `?documentType=${this.documentType}&ses=${this.sessionHash}&key=${this.sessionId}${forceDownloadPair}`;
    }

    return searchParams;
  }

  exportDocument(format, autoPrint) {
    const self = this;
    const search = this.getSearchParams() || window.location.search;
    // Only check if we have credits here. The credit deduction will happen
    // on the server when the actual export fires.
    const offerManager = new OfferManager();
    offerManager.requestExport(
      () => {
        ExportDocument.exportLoading('start', format, () => {
          // This step could actually take a while with no actual progress so
          // we want to message that allow the progressbar to catchup, then
          // trigger the download.
          setTimeout(() => {
            let password;
            if (window.PDFViewerApplication.passwordPrompt.reason) {
              password = prompt(
                'Please re-enter the password to export this PDF file',
                '',
              );
            }
            self.triggerServerDocumentDownload(format, password, autoPrint);
          }, 200);
        });
      },
      self.newAccountPath + search,
      true,
      search,
    );
  }

  exportPDF(autoPrint) {
    // The flow of the PDF export
    this.exportDocument('pdf', autoPrint);
  }

  exportDocx() {
    // The full of the docx export
    this.exportDocument('docx');
  }

  documentDownloadSuccess(format, autoPrint) {
    ExportDocument.deductCredit();

    // Allow some time for the print menu or the download to occur in the
    // browser before hiding the loading bar
    setTimeout(ExportDocument.stopExport, 500, false, autoPrint);

    // it looks like chrome explicitly removed the autoprint feature from pdfs as of v77
    // https://pdfium.googlesource.com/pdfium.git/+/2021804f1b414c97667c03d7ab19daf66f6a19ef
    // as a workaround, programatically trigger print
    ExportDocument.enableFallbackAutoPrint(autoPrint);

    if (this.isMembersAreaExport) {
      window._track(
        'Download Document',
        `${this.documentType} | Member's Area`,
      );
    } else {
      window._track(
        `Freewrite Export - ${format.toUpperCase()}`,
        'Server Side | Member',
      );
    }
  }

  trackDownloadFail(format, isExportService = false) {
    if (this.isMembersAreaExport) {
      window._track(
        'Download Document - Failed',
        `${this.documentType} | Member's Area`,
      );
    } else {
      const exportType = isExportService ? 'Async Export' : 'Legacy Export';
      window._track(
        `Freewrite Export - Failed ${format.toUpperCase()}`,
        `${exportType} | Member`,
      );
    }
  }

  documentDownloadFail(responseText, format) {
    // if the response is parsable, it's an problem with their account and we should
    // handle the failure accordingly. Normally we'd check the response code but this
    // fileDownload library doesn't allow that.
    try {
      const resp = JSON.parse(responseText);
      ExportDocument.handleExportPermissionsFailure(resp);
    } catch (e) {
      // the response isn't parsable, something went wrong.
      ExportDocument.stopExport(false);
      this.trackDownloadFail(format);
      alert('Sorry, we couldn\'t export your document. Please contact customer service at support@formswift.com or call us at 1-888-311-2977');
    }
  }

  static forceDownload() {
    return window.location.search.indexOf('force_download') >= 0;
  }

  async triggerServerDocumentDownload(format, password, autoPrint) {
    const paramStr = this.getParamString(format, password, autoPrint);
    const self = this;

    $.fileDownload(
        `/free-write/scripts/generate_document.php?referer=${window.location.pathname}`,
      {
        successCallback: () => {
          self.documentDownloadSuccess(format, autoPrint);
        },
        failCallback: (responseText) => {
          self.documentDownloadFail(responseText, format);
        },
        httpMethod: 'POST',
        data: paramStr,
      },
    );
  }

  static getDownloadFilename(response, format) {
    const headerFilename = (response.headers.get('Content-Disposition') || '')
      .split(';')
      .filter(segment => segment.trim().startsWith('filename='))
      .map(segment => segment
        .trim()
        .replace('filename=', '')
        .replace(/^["']/, '')
        .replace(/["']$/, ''),
      );
    return headerFilename[0] || `document.${format}`;
  }

  static handleExportServiceDownloadFail(message) {
    const { OfferManager: OfferManagerReference } = support;
    const offerManager = new OfferManagerReference();
    switch (message) {
      case 'sign-in-required':
        window.location.assign(`/signIn.php?redirect_url=${encodeURI(window.location.href)}`);
        break;
      case 'user-cancelled':
        offerManager.showReactivationModal();
        break;
      default:
        window.location.assign(`/myDocuments.php?error=${message}`);
        break;
    }
  }

  async exportServiceDownloadFail(format, response) {
    try {
      const json = await response.json();
      ExportDocument.handleExportServiceDownloadFail(json.message);
    } catch (e) {
      ExportDocument.stopExport(false);
      this.trackDownloadFail(format, true);
      alert('Sorry, we couldn\'t export your document. Please contact customer service at support@formswift.com or call us at 1-888-311-2977');
    }
  }

  async exportServiceDownloadSuccess(format, response, autoPrint) {
    const blob = await response.blob();
    support.fileDownload(blob, ExportDocument.getDownloadFilename(response, format));
    this.documentDownloadSuccess(format, autoPrint);
  }

  async triggerExportServiceDocumentDownload(format, password, autoPrint) {
    const data = {
      document_type: window.DocumentSettings.type,
      format,
      session_hash: window.DocumentSettings.ses,
      session_id: parseInt(window.DocumentSettings.key, 10),
      user_id: parseInt(window.COOKIE.get('userid'), 10),
      user_token: window.COOKIE.get('userToken'),
    };
    if (ExportDocument.forceDownload()) {
      data.force_download = true;
    }

    if (password) {
      data.password = password;
    }

    const response = await support.fetch(`${window.EXPORT_URL}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      referrerPolicy: 'no-referrer-when-downgrade',
      body: JSON.stringify(data),
    });

    if (response.ok) {
      await this.exportServiceDownloadSuccess(format, response, autoPrint);
    } else {
      await this.exportServiceDownloadFail(format, response);
    }
  }

  processExport(shareKey, print) {
    // Start the Export process (this is what the main button calls)
    window.DocumentSettings.shareFile = false;
    window.DocumentSettings.shareKey = false;

    // We're exporting for Sharing so we can enable scrolling and set the share keys
    if (shareKey) {
      window.DocumentSettings.shareFile = 'share_file';
      window.DocumentSettings.shareKey = shareKey;
    }

    this.exportPDF(print);
  }

  createExportTool() {
    // Create the export button
    const $exportTool = $('#download_button-modal');
    $exportTool.click(() => {
      let shareKey = null;
      if (window.DocumentSettings && window.DocumentSettings.shareKey) {
        shareKey = window.DocumentSettings.shareKey;
      }
      this.processExport(shareKey);
    });
  }
}



// WEBPACK FOOTER //
// ./src-assets/js/export-document.js